<template>
  <div style="margin-right: 1em;">
    <div 
      v-if="errorChoices!=null"
      class="form-row"
    >
      <div
        class="alert alert-primary d-flex align-items-center w-100"
        style="font-size: 0.9rem;"
        role="alert"
      >
        <i
          class="bi bi-info-circle-fill"
          style="margin-right: 1em;"
        />
        <div>
          {{ $t('resource.ftp.info') }}
        </div>
      </div>
    </div>
    <div 
      v-if="errorChoices!=null" 
      class="form-row"
    >
      <div
        class="alert alert-warning d-flex align-items-center w-100"
        style="font-size: 0.9rem;"
        role="alert"
      >
        <div>
          {{ errorChoices }}
        </div>
      </div>
    </div>
    <div 
      class="form-row"
    >
      <b-overlay
        id="overlay-background"
        :show="loading"
        :variant="'white'"
        :opacity="0.7"
        :blur="'2px'"
        rounded="sm"
        no-wrap
      />
      <div class="form-group col-6">
        <label 
          class="required"
          style="margin-top: 1em;"
        >
          {{ $t('resource.format.label') }}
        </label>
        <p>
          {{ $t('resource.format.help') }}
        </p>
        <ValidatedFileFormatSelector
          :required="isRequired"
          validation-name="Format FTP"
          @select="$event => form.format = $event"
        />
        <p style="margin-top: 0.5em; font-size: 0.85em; white-space: wrap;">
          {{ $t('resource.fileUpload.formatHelp') }}
        </p>
      </div>
    </div>
    <div 
      class="form-row"
    >
      <div class="form-group col">
        <label 
          class="required"
          style="margin-top: 1em;"
        >
          {{ $t('resource.ftp.files.label') }}
        </label>
        <p>
          {{ $t('resource.ftp.files.help') }}
        </p>
        <ValidationProvider
          v-slot="{ classes, errors }"
          name="Fichier FTP"
          :rules="`${isRequired ? 'required' : ''}`"
        >
          <div
            class="control"
            :class="isRequired ? classes : ''"
          >
            <Multiselect
              v-model="form.selectedFtpFiles"
              class="ftp-choices"
              :options="ftpChoices"
              :multiple="true"
              track-by="value"
              label="label"
              select-label=""
              selected-label=""
              deselect-label=""
              :searchable="false"
              :close-on-select="false"
              :placeholder="$t('resource.ftp.files.placeholder')"
              @select="onSelect"
              @remove="onRemove"
            >
              <span
                slot="option"
                slot-scope="scope"
                class="checkbox-label"
                @click.self="onSelect(scope.option)"
              >
                {{ scope.option.label }}
                <input
                  v-model="scope.option.checked"
                  class="checkbox"
                  type="checkbox"
                  @focus.prevent
                >
              </span>
            </Multiselect>
            <span class="form-errors">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import Multiselect from 'vue-multiselect';
import ValidatedFileFormatSelector from '@/common/components/ValidatedFileFormatSelector.vue';
import {
  ftpAPI
} from '@/api';
import { ValidationProvider, extend, configure } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';


extend('required', {
  ...required,
  message: 'Ce champ est requis'
});
configure({
  classes: {
    valid: 'is-valid',
    invalid: 'is-invalid'
  }
});

export default {
  name: 'FTPCreator',

  components: {
    Multiselect,
    ValidationProvider,
    ValidatedFileFormatSelector,
  },

  props: {
    required: {
      type: Boolean,
      default: true
    },
    isMain: {
      type: Boolean,
      default: true
    },
  },

  data() {
    return {
      loading: false,
      isRequired: true,
      errorChoices: null,
      ftpChoices: [],
      form: {
        type: 'ftp',
        format: null,
        selectedFtpFiles: []
      },
    };
  },

  computed: {
    ...mapState('datasets', [
      'currentDatasetUsergroup'
    ]),

    isGeospatial() {
      return this.form.format && this.form.format.type.value === 'geospatial';
    },
    isXY() {
      return this.form.format && this.form.format.type.value === 'tabular-xy';
    }
  },

  watch: {
    required(newValue) {
      this.isRequired = newValue;
    },
    form: {
      deep: true,
      handler(newValue) {
        if (newValue.format || newValue.selectedFtpFiles.length) {
          this.setRequired(true);
        } else {
          this.setRequired(false);
        }
      }
    },
    'form.selectedFtpFiles': {
      deep: true,
      handler: async function() {
        this.setResourceFTP();
      }
    },
    'form.format': function() {
      this.setResourceFTP();
    },
    isGeospatial(newValue) {
      this.$emit('geo', newValue);
      this.$emit('xy', this.form.format && this.form.format.type.value === 'tabular-xy');
    },
    isXY(newValue) {
      this.$emit('xy', newValue);
    }
  },

  async created() {
    try {
      this.loading = true;
      this.isRequired = this.required;
      this.$emit('geo', this.isGeospatial);
      this.$emit('xy', this.isXY);
      
      if (this.currentDatasetUsergroup && this.currentDatasetUsergroup.id) {
        const response = await ftpAPI.getFTPChoices(this.currentDatasetUsergroup.id);
        this.ftpChoices = response.map(el => { return { ...el, checked: false }; });
        this.ftpChoices.sort((a, b) => {
          return a.label.localeCompare(b.label);
        });
        if(this.ftpChoices.length===0){
          this.errorChoices="Aucun fichier FTP disponible pour ce groupe d'utilisateurs.";
          this.$emit('error', this.errorChoices);
        } else {
          this.errorChoices=null;
        } 
        
      }
      this.loading = false;
    } catch (err) {
      if(err.response && err.response.data && err.response.data.detail){
        this.errorChoices = err.response.data.detail;
        this.$emit('error', this.errorChoices);
      }
      console.error(err);
      this.loading = false;
    }
  },

  methods: {
    

    setRequired(e) {
      this.isRequired = e;
      this.$emit('set', {
        required: this.isRequired
      });
    },

    onSelect (option) {
      option.checked = true;
    },
    
    onRemove (option) {
      option.checked = false;
    },

    setResourceFTP() {
      this.$emit('set', {
        form: this.form,
        required: this.isRequired
      });
    }
  }
};
</script>
